import { useEffect, useRef } from "react";

interface Props {
  open: boolean;
  onCancel: () => void;
  // When present, the modal is a "pre-record" prompt. Clicking the primary
  // button calls onStart (which typically both accepts + begins recording).
  // When absent, the modal is a read-only "review" view opened from the
  // Learn more link, and the primary button becomes a plain Close.
  onStart?: () => void;
}

export default function RecordingDisclaimer({
  open,
  onCancel,
  onStart,
}: Props) {
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  // Focus the primary button when the modal opens so keyboard users land
  // somewhere sensible. Don't trap focus fully — the modal has at most two
  // buttons, and the backdrop blocks pointer interaction with the app behind.
  useEffect(() => {
    if (open) primaryRef.current?.focus();
  }, [open]);

  // Block background scroll while the modal is up.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const isPrompt = typeof onStart === "function";

  return (
    <div
      className="modal-backdrop"
      // Intentionally no onClick here — clicking outside must not dismiss.
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="recording-disclaimer-title"
        className="modal-card"
      >
        <h2
          id="recording-disclaimer-title"
          className="modal-title"
        >
          Before you record
        </h2>

        <p>
          Call recording laws vary by location. In many US states — including
          Illinois, California, and others — all parties on the call must
          consent to being recorded.
        </p>

        <p>Before you start recording, tell the other party:</p>

        <blockquote className="modal-quote">
          "I'd like to let you know that I'm recording this call for my own
          records."
        </blockquote>

        <p className="modal-affirmation">
          By continuing you confirm that you have informed all parties on this
          call that it is being recorded, and that recording is lawful in your
          location. CaseThread is not responsible for unlawful recording.
        </p>

        <div className="modal-actions">
          {isPrompt ? (
            <>
              <button type="button" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                ref={primaryRef}
                onClick={onStart}
              >
                I've informed all parties — start recording
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn-primary"
              ref={primaryRef}
              onClick={onCancel}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
